import Auth from '@aws-amplify/auth';
import { Alert, Button, Input, LinkButton, Spinner, theme } from '@salecycle/ui-component-library';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import LoginFormHeader from './loginFormHeader';
import PasswordStrength from './passwordStrength';
import { AlertContainer, SubmitContainer, SubmitButtonContainer } from './styles';

export interface Props {
  changeState: (state: any, data?: any, emailAddress?: string) => void;
}

export const ForgotPassword: FC<Props> = ({ changeState }) => {
  const { t } = useTranslation('login');
  const [isLoading, setIsLoading] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [sendCodeClicked, setSendCodeClicked] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordStrength, setPasswordStrength] = React.useState(0);
  const [submitButtonDisabled, setSubmitButtonDisabled] = React.useState(false);

  const renderConfirmationForm = () => {
    return (
      <>
        <LoginFormHeader
          heading={t('resetPassword.heading')}
          description1={t('resetPassword.enterCode.description1', { email })}
          description2={t('resetPassword.enterCode.description2')}
        />

        {errorMessage ? (
          <AlertContainer>
            <Alert heading={errorMessage} variation="error" />
          </AlertContainer>
        ) : null}

        <form onSubmit={handleSubmitNewPassword}>
          <Input
            inputTestId="UniqueCode"
            ariaLabel={t('resetPassword.enterCode.input.label')}
            label={t('resetPassword.enterCode.input.label')}
            inputId="UniqueCodeInput"
            required={true}
            onChange={handleUniqueCodeChange}
            key="unique code"
            value={code}
            hint={t('resetPassword.enterCode.input.hint')}
            placeholder={t('resetPassword.enterCode.input.placeholder')}
          />
          <PasswordStrength handleFormChange={handleFormChange} />

          <SubmitContainer>
            <SubmitButtonContainer>
              <Button
                variation="primary"
                type="submit"
                onClick={handleSubmitNewPassword}
                disabled={submitButtonDisabled || isLoading}
              >
                {t('resetPassword.btnSubmit')}
              </Button>

              {isLoading && <Spinner />}
            </SubmitButtonContainer>
          </SubmitContainer>

          <SubmitContainer>
            <LinkButton onClick={handleBackToSignIn}>{t('resetPassword.returnButton')}</LinkButton>
          </SubmitContainer>

          <SubmitContainer>
            <LinkButton onClick={handleSendCodeClicked}>{t('resetPassword.resendCodeButton')}</LinkButton>
          </SubmitContainer>
        </form>
      </>
    );
  };

  const handleUniqueCodeChange = (event: any) => {
    setCode(event);
    handleFormChange(passwordStrength, password);
  };

  // Step 1 - send code to users email
  const handleSendCodeClicked = async (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    const cleanedEmail = email.toLowerCase();
    try {
      await Auth.forgotPassword(cleanedEmail);
      setSendCodeClicked(true);
      setErrorMessage('');
    } catch (e) {
      //Show reset password box. No code has been sent
      setSendCodeClicked(true);
    }
    setIsLoading(false);
  };

  // Step 2 - user sends code and password
  const handleSubmitNewPassword = async (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    Auth.forgotPasswordSubmit(email, code, password)
      .then(() => setConfirmed(true))
      .catch(err => {
        if (err.code === 'UserNotFoundException') {
          setErrorMessage(t('errors.invalidCodeException'));
        } else {
          setErrorMessage(err.message);
        }
      });

    setIsLoading(false);
  };

  const handleBackToSignIn = (success: any) => {
    changeState('signIn', success, email);
  };

  const handleFormChange = (strength: any, password: string) => {
    if (strength === 5 && code.length > 0) {
      setSubmitButtonDisabled(false);
      setPasswordStrength(strength);
      setPassword(password);
    } else {
      if (submitButtonDisabled !== true || code.length === 0) {
        setSubmitButtonDisabled(true);
        setPasswordStrength(strength);
        setPassword(password);
      } else {
        setPasswordStrength(strength);
        setPassword(password);
      }
    }
  };

  const renderRequestCodeForm = () => {
    return (
      <>
        <LoginFormHeader
          color={theme.designToken.newPrimary}
          heading={t('resetPassword.heading')}
          description1={t('resetPassword.enterCode.description3')}
        />

        {errorMessage && (
          <AlertContainer>
            <Alert heading={errorMessage} variation="error" />
          </AlertContainer>
        )}

        <form onSubmit={handleSendCodeClicked}>
          <Input
            inputTestId="EmailInput"
            ariaLabel={t('resetPassword.emailInput')}
            label={t('resetPassword.emailInput')}
            color={theme.designToken.newPrimary}
            inputId="EmailAddressInput"
            required={true}
            onChange={e => setEmail(e)}
            key="email"
            placeholder={t('resetPassword.emailInput')}
          />

          <SubmitContainer>
            <SubmitButtonContainer>
              <Button variation="newPrimary" onClick={handleSendCodeClicked} disabled={isLoading}>
                {t('resetPassword.sendCodeButton')}
              </Button>

              {isLoading && <Spinner />}
            </SubmitButtonContainer>
          </SubmitContainer>

          <SubmitContainer>
            <LinkButton
              color={theme.designToken.newPrimary}
              hoverColor={theme.designToken.newSecondary}
              onClick={handleBackToSignIn}
            >
              {t('resetPassword.returnButton')}
            </LinkButton>
          </SubmitContainer>
        </form>
      </>
    );
  };

  return (
    <>
      {!sendCodeClicked
        ? renderRequestCodeForm()
        : !confirmed
        ? renderConfirmationForm()
        : handleBackToSignIn('success')}
    </>
  );
};

export default ForgotPassword;
